/**
 * THE CABINET'S ROUTER — the one pass that makes the already-written screens reachable.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE DID NOT EXIST UNTIL NOW, AND WHAT IT CHANGES
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `@tanstack/react-router` is ruled by `adr/0007-web-app-stack.md` and was NOT installed, so every
 * screen in this app was written as a plain component behind a route DESCRIPTOR and nothing rendered
 * it. `features/cabinet/routes.ts` says so in its own header and even writes out the wiring line it
 * was waiting for:
 *
 *     CABINET_ROUTES.map(({ path, Component }) => createRoute({ path, component: Component }))
 *
 * That is what this file does, once, for three descriptor sets. Measured before the change: a
 * reachability walk from `main.tsx` reached **3 of 121** source files in this app, because `main.tsx`
 * rendered `App` and `App` is M0's one-screen shell — which says in its own header *"It is not the
 * cabinet"*. The router is the single cheapest unblock in the plan.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ `/` STILL RENDERS THE M0 SHELL, AND THAT IS DELIBERATE — TWO GATE ASSERTIONS READ IT
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `s11.7` renders "both SPA shells" by visiting `./` on the BUILT app and requires zero accessibility
 * violations, and the deploy smoke check `adr/0007` loads both roots and "expects the app shell"
 * because *"a wrong one is a white page"*. Moving the shell off `/` would turn both of those from a
 * proof into a different proof, silently. So the shell keeps `/`, and the cabinet screens are
 * mounted BESIDE it. The sign-in track has landed and read this paragraph: it did NOT take `/`. The
 * shell stays session-free and only gained links to the two doors, and a completed sign-in lands on
 * `CABINET_HOME_PATH` (`/dashboard`, `features/auth/navigation.ts`) instead.
 *
 * ★ ROUTES AND LABELS ARE NOT THE OWNER'S. `features/cabinet/routes.ts` records the rulings
 * (decision-log rows 99, 100 and 108, restated at `09-authors-app.md`): *"layout, wording,
 * navigation and screen flow are the app's own business, settled in the build and never brought to
 * the owner"*. The one route the corpus fixes anywhere is `/enter`, the authenticated hand-off, and
 * it belongs to the sign-in track rather than to this file.
 *
 * ★★ `/apply` IS MOUNTED, and of the two props it once waited for (`routes/apply/index.tsx`,
 * both optional) it now gets one. `onOpenCabinet` moves the approved applicant to the cabinet's
 * home through the router. `getIdToken` is NOT passed, on purpose: the application seam defaults it
 * to the app's one token source (`features/application/api/create.ts`, `createApplicationApi`), the
 * same default every other seam takes, so a token source is never threaded through a routing file.
 *
 * WHAT THE OMISSION WAS COSTING, measured rather than asserted: `features/support/navigation.ts`
 * already declares `application: '/apply'` as a place the support screens send somebody, and the
 * default `Navigate` is `window.location.assign` (`navigation.ts`) — so that link was a full
 * page load into the root's own not-found, on a route the corpus's own Caddy fragment serves
 * (`infra/web/nginx.conf`, `try_files $uri /index.html`). And A2 acceptance
 * step 10 (`docs/design/12-build-plan.md`) requires "the application form" to join the
 * automated accessibility check, which visits a built static root and can only reach a screen the
 * router answers for. A screen nothing routes to cannot be checked, and a check pointed at a screen
 * that did not render is worse than no check.
 *
 * ★★ THE SESSION. `AuthSessionProvider` is mounted by the ROOT route, so every route - the two
 * unauthenticated doors included - reads one identity context. `/sign-in` and `/enter` are mounted
 * from `authRoutes` (`routes/(auth)/index.tsx`), the only two routes the corpus leaves
 * unauthenticated (adr/0007-web-app-stack.md). Every other route except `/` sits inside
 * `SessionGate`, and THE GATE ONLY EXISTS IN A LIVE BUILD: with `VITE_AUTHORS_API_LIVE` unset the
 * gate renders its screen untouched, so the default build - fixtures, nobody signed in, the build
 * the accessibility leg checks - renders exactly what it rendered before the session existed. In a
 * live build a signed-out visitor goes to `/sign-in?next=<where they were>`, and a signed-in reader
 * with no author row who opens a cabinet screen goes to `/welcome` (features/auth/session/gate.ts).
 *
 * ★ NO FILE-BASED ROUTING AND NO CODE GENERATION. TanStack's generator would make the descriptor
 * files dead and move the route list into filenames; the corpus deliberately built the routes as
 * DATA, with the operations each screen calls listed beside it so the coverage stays countable
 * (`CABINET_OPERATIONS_COVERED`). Consuming that data keeps one list, not two.
 *
 * ★★ REPORTED, NOT WORKED AROUND: THIS TOOK THE BUNDLE TO ONE CHUNK OF ~1.0 MB (~319 kB gzipped),
 * and Vite says so on every build. It is the direct consequence of the fix — 198 files that nothing
 * rendered are now reachable — so it is a real number appearing for the first time rather than a
 * regression. **Nothing in the gate asserts a bundle budget and `02-nfr.md` sets no front-end size
 * or page-load target**, both checked rather than assumed, so this breaks no rule today. It will
 * matter: every screen is in the first chunk, including the Tiptap editor, and route-level
 * `lazy()` splitting is the ordinary answer. Left undone deliberately — splitting before the
 * screens are finished would optimise a shape that is still changing, and inventing a budget the
 * NFRs decline to set is not this file's to do.
 */
import { useCallback, useMemo } from 'react';
import type { FunctionComponent } from 'react';
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import type { AnyRoute, RouterHistory } from '@tanstack/react-router';
import { App } from './App';
import { isLiveApiConfigured } from './config';
import { CABINET_HOME_PATH, routerNavigation } from './features/auth/navigation';
import { AuthSessionProvider, SessionGate, useAuthSession } from './features/auth';
import { WELCOME_PATH, WelcomeRoute } from './routes/welcome';
import type { GateRequirement } from './features/auth/session/gate';
import { CABINET_ROUTES } from './features/cabinet/routes';
import { CabinetShell } from './features/shell/CabinetShell';
import { CornerLanguagePicker } from './features/shell/LanguagePicker';
import { applyInsideShellFor } from './features/shell/cabinet-navigation';
import { RouterNavigateProvider } from './features/shell/RouterNavigateProvider';
import { CabinetNotFound, authRoutes, landingPathAmong } from './routes/(auth)';
import type { AuthRouteDescriptor } from './routes/(auth)';
import { APPLY_ROUTE, ApplyRoute } from './routes/apply';
import { SUPPORT_ROUTES } from './routes/support/routes';
import { PROFILE_PREVIEW_ROUTE, ProfilePreviewRoute } from './routes/profile-preview';
/**
 * The root renders the session provider, the app's two navigation seams bound to this router
 * (`features/shell/RouterNavigateProvider.tsx`), and its child - nothing else. The cabinet's header
 * and sidebar are NOT here: `gated` below puts `CabinetShell` around each screen that belongs in it,
 * since `/terms` must NOT show the cabinet nav. The providers are here and not in `main.tsx` so a
 * test mounting this tree gets the app's providers; neither renders markup of its own.
 *
 * An address no route answers draws `CabinetNotFound` (routes/(auth)/not-found.tsx): no session,
 * no shell, one sentence and the way back - it used to be the router's bare "Not Found".
 */
function RootLayout() {
  return (
    <AuthSessionProvider>
      <RouterNavigateProvider>
        <Outlet />
      </RouterNavigateProvider>
    </AuthSessionProvider>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: outsideTheShell(CabinetNotFound),
});

/** The router's history as the `Navigation` the auth screens take - see `routerNavigation`. */
function useRouterNavigation() {
  const router = useRouter();
  return useMemo(() => routerNavigation(router.history), [router]);
}

/**
 * One of the two unauthenticated doors. A completed sign-in or hand-off REPLACES the door with where
 * the person was going - a carried `next` that names a screen this app answers (`landingPathAmong`),
 * else the cabinet's home - so Back never reopens a sign-in form or a spent hand-off code. Exported
 * for `test/router-session.spec.tsx`, which mounts it over stub destinations.
 */
export function AuthDoor({ descriptor }: { readonly descriptor: AuthRouteDescriptor }) {
  const navigation = useRouterNavigation();
  const onSignedIn = useCallback(() => {
    navigation.replace(landingPathAmong(navigation.currentSearch(), CABINET_ROUTE_PATHS));
  }, [navigation]);
  return descriptor.element({ onSignedIn, navigation });
}

/**
 * A screen behind the live-only gate. A reader with no author row is sent to `/welcome` instead.
 * `insideShell` puts the cabinet's header and sidebar around the screen - INSIDE the gate, so a
 * reader the gate is still deciding about, or sending elsewhere, never sees a sidebar of screens
 * that would refuse them.
 */
function gated(
  Screen: FunctionComponent,
  requirement: GateRequirement,
  insideShell: boolean,
): FunctionComponent {
  return mainUnlessInShell(insideShell, function GatedScreen() {
    return (
      <SessionGate requirement={requirement} notReachablePath={WELCOME_PATH} main={insideShell}>
        {insideShell ? (
          <CabinetShell>
            <Screen />
          </CabinetShell>
        ) : (
          <Screen />
        )}
      </SessionGate>
    );
  });
}

/**
 * `/` — M0's shell in the DEFAULT build. See the header: two gate assertions read this exact address.
 * In a LIVE build a page of links at the bare address helps nobody (the owner, 13 October 2026: "should
 * not it be like auth opened right away?"), so it goes straight to the cabinet's home, whose gate sends
 * a signed-out visitor to `/sign-in`. Neither assertion moves: the accessibility leg reads the default
 * build, and the deploy smoke check reads the HTML nginx serves, which a client-side redirect leaves alone.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    if (isLiveApiConfigured()) {
      throw redirect({ href: CABINET_HOME_PATH, replace: true });
    }
  },
  component: outsideTheShell(App),
});

/**
 * `/apply` — the application form, and an approved author's profile. Any signed-in person may
 * apply, so its gate asks only for a session (`routes/apply/index.tsx`); `onOpenCabinet` is
 * the one prop passed - see the header. Its frame is `ApplyFrame`'s, at the foot (design 14, F13).
 */
function ApplyPage() {
  const navigation = useRouterNavigation();
  const onOpenCabinet = useCallback(() => {
    navigation.push(CABINET_HOME_PATH);
  }, [navigation]);
  return (
    <SessionGate requirement="signed-in" notReachablePath={APPLY_ROUTE.path} main>
      <ApplyFrame onOpenCabinet={onOpenCabinet} />
    </SessionGate>
  );
}

const applyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: APPLY_ROUTE.path,
  component: ApplyPage,
});

/** `/sign-in` and `/enter`. Never gated: they are where the gate sends people. */
const authRouteNodes = authRoutes.map((descriptor) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: descriptor.path,
    component: outsideTheShell(function AuthDoorRoute() {
      return <AuthDoor descriptor={descriptor} />;
    }),
  }),
);

const cabinetRoutes = CABINET_ROUTES.map((route) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: route.path,
    component: gated(route.Component, 'cabinet', true),
  }),
);

/**
 * `/support` and `/terms` ask only for a session: the support page already renders a 403 on the
 * contacts read as its own state (`features/support/api/errors.ts`), so a reader with no
 * author row is shown the page rather than bounced off it.
 */
const supportRoutes = SUPPORT_ROUTES.map((route) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: route.path,
    component: gated(route.Component, 'signed-in', route.insideShell),
  }),
);

/**
 * Every path this app answers, as data, so a test can assert the set without rendering anything.
 * Exported for `router.spec.tsx`, which is the guard that a descriptor added to either list reaches
 * the router — the failure this whole file exists to end.
 */
export const CABINET_ROUTE_PATHS: readonly string[] = [
  // The literal, not `shellRoute.path`: TanStack normalises a route's `path` relative to its parent
  // once the tree is built, so reading it back here would describe the library rather than the app.
  // `/` is a named address in this file for the two gate assertions above — it is declared as one.
  '/',
  APPLY_ROUTE.path,
  ...authRoutes.map((route) => route.path),
  ...CABINET_ROUTES.map((route) => route.path),
  ...SUPPORT_ROUTES.map((route) => route.path),
  ...ownFramePaths(),
];

/**
 * `/apply/preview` - "Preview your public page" (design 24, T28): asked of the same session as
 * `/apply` and drawn in the same frame (`ProfilePreviewPage`, at the foot), so an applicant opens it
 * too. Declared here rather than beside `applyRoute` because the lines above are cited by number.
 */
const profilePreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: PROFILE_PREVIEW_ROUTE.path,
  component: ProfilePreviewPage,
});

/**
 * `/welcome` - "Write for MoonX" (design 25, section 6): where the gate sends a reader with no
 * author row, and the two steps after "Become an author". It asks only for a session - the reader
 * it is for has no cabinet screen - and is drawn in its own frame, the doors' card, with the
 * router's one `main` around the gate, as `/terms` is.
 */
const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: WELCOME_PATH,
  component: outsideTheShell(function WelcomePage() {
    return (
      <SessionGate requirement="signed-in" notReachablePath={WELCOME_PATH}>
        <WelcomeRoute />
      </SessionGate>
    );
  }),
});

export const routeTree = rootRoute.addChildren([
  shellRoute,
  applyRoute,
  profilePreviewRoute,
  welcomeRoute,
  ...authRouteNodes,
  ...cabinetRoutes,
  ...supportRoutes,
] as AnyRoute[]);

/**
 * The app's router over a given history. `main.tsx` uses the default (the browser's); a test
 * passes a memory history and gets the same tree, the same root provider and the same gates.
 */
export function createCabinetRouter(history?: RouterHistory) {
  return createRouter({ routeTree, ...(history === undefined ? {} : { history }) });
}

export const router = createCabinetRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/* ======================================================================= one `main` on every page */

/**
 * ★ ONE `main` LANDMARK ON EVERY PAGE, AND WHICH ELEMENT IT IS DEPENDS ON THE SHELL (register R-069).
 * A screen inside `CabinetShell` has its `main` already: `AppShell.Main`. The pages this file mounts
 * WITHOUT the shell - `/`, the doors, `/apply` with no author row (and in the default build),
 * `/terms`, `/welcome` and the not-found - had none, so a screen reader had no main region, and axe
 * counted every line of them as content outside any landmark (`landmark-one-main`, `region`). The
 * accessibility leg asserts both on each of them now
 * (web/ui/test/e2e/cabinet-structure-accessibility.spec.ts).
 *
 * ★ AND EVERY ONE OF THEM HAS THE LANGUAGE PICKER IN ITS TOP CORNER (design 29, W5; the owner, 19
 * October: hard to get through sign-in and become an author without understanding the page). They
 * have no Display settings, the cabinet's place for it. First in the `main`, so it is inside the one
 * landmark and the first stop for the keyboard (features/shell/LanguagePicker.tsx).
 *
 * THE LANDMARK GOES AROUND THE GATE, NOT INSIDE IT, so the gate's own "Opening your cabinet" wait on
 * `/terms` is inside it too. Written at the foot because the lines above are cited by number; both
 * are function declarations, which are hoisted, so the routes above can call them. `/apply` is the
 * one page whose frame is decided only once the gate has let the reader in, so its gate carries its
 * own `main` (R-071's `main` prop) and `ApplyFrame` below draws the rest.
 */
function outsideTheShell(Screen: FunctionComponent): FunctionComponent {
  function OutsideTheShell() {
    return (
      <main>
        <CornerLanguagePicker />
        <Screen />
      </main>
    );
  }
  OutsideTheShell.displayName = `OutsideTheShell(${Screen.displayName ?? Screen.name})`;
  return OutsideTheShell;
}

/**
 * `gated`'s half of the rule: a screen drawn inside `CabinetShell` has its `main` already - and the
 * gate's own wait in front of it, drawn before the shell, carries one of its own (R-071, `gated`
 * passes `main={insideShell}` to `SessionGate`).
 */
function mainUnlessInShell(insideShell: boolean, Screen: FunctionComponent): FunctionComponent {
  return insideShell ? Screen : outsideTheShell(Screen);
}

/* =================================================================== `/apply`'s frame (F13) */

/**
 * ★ WHICH FRAME `/apply` IS DRAWN IN - design 14, finding F13 (the cabinet walk of 2026-10-01).
 *
 * WHAT WAS WRONG. "Your profile" in the sidebar opens `/apply`, where an approved author edits their
 * profile, and `/apply` was mounted outside the shell for everybody: following the entry dropped
 * the sidebar, and the way back was the page's own "Go to your cabinet" button. Every other screen
 * the sidebar lists keeps the sidebar.
 *
 * WHAT IT IS NOW. Decided by `applyInsideShellFor` (features/shell/cabinet-navigation.ts), which
 * asks the sidebar's own question - is this reader offered the page as "Your profile" or as "Your
 * application"? - of the live session:
 *   - anybody with an author row - an applicant since design 25 (D6: the cabinet is open to them,
 *     empty), an approved author, a disabled one - gets `CabinetShell`, whose `AppShell.Main` is
 *     the page's one `main`, with "Author profile" lit;
 *   - a reader with no author row keeps the page's own frame, a bare `main`: the shell sits inside
 *     the gate precisely so that a reader the cabinet is not open to never sees a sidebar of
 *     screens that would refuse them (`gated`, above);
 *   - the DEFAULT build, which has no session to ask, keeps the page's own frame exactly as before -
 *     its fixture application is an applicant's, and the accessibility leg measures `/apply` there
 *     as a page outside the shell (web/ui/test/e2e/cabinet-structure-accessibility.spec.ts).
 *
 * INSIDE THE GATE, like `gated`'s shell, so the frame is chosen from a session that is loaded. The
 * gate's own wait carries the `main` (`ApplyPage` passes `main`), exactly as `outsideTheShell`'s
 * `main` around the gate used to give it one.
 *
 * ★ AND ONLY THE PAGE OUTSIDE THE SHELL IS GIVEN THE WAY IN (the owner's live walk of real cabinets,
 * 2026-10-01). Inside the shell an approved author's header offered "Go to your cabinet" beside the
 * cabinet's own sidebar - a button to where they already were. So `onOpenCabinet` reaches the page
 * only in its own frame, and the page reads its absence as "inside" (`stepFor`, at the foot of
 * features/application/screens/ApplicationScreen.tsx): the header then names the profile and
 * offers nothing. Outside - an applicant just approved whose session has not caught up, or the
 * default build - the button stays, because there it is the only way in.
 */
function ApplyFrame({ onOpenCabinet }: { readonly onOpenCabinet: () => void }) {
  const { state } = useAuthSession();
  return applyInsideShellFor(state) ? (
    <CabinetShell>
      <ApplyRoute />
    </CabinetShell>
  ) : (
    <main>
      <CornerLanguagePicker />
      <ApplyRoute onOpenCabinet={onOpenCabinet} />
    </main>
  );
}

/* ======================================================= `/apply/preview`'s frame (design 24, T28) */

/**
 * "Preview your public page" - the author's public page as readers would see it, from the saved
 * profile (routes/profile-preview/index.tsx), and two cabinet reads for its figures - a refused
 * one drops only that row (batch 2). `/apply`'s rules: the gate asks for a session and nothing more,
 * so an applicant opens it too, and its own wait carries the `main`. The frame is `ApplyFrame`'s
 * question, asked of the same session: inside the shell for anybody with an author row ("Author
 * profile" lit: the address is under `/apply`), its own frame with no row and in the default build.
 */
function ProfilePreviewPage() {
  return (
    <SessionGate requirement="signed-in" notReachablePath={APPLY_ROUTE.path} main>
      <ProfilePreviewFrame />
    </SessionGate>
  );
}

function ProfilePreviewFrame() {
  const { state } = useAuthSession();
  return applyInsideShellFor(state) ? (
    <CabinetShell>
      <ProfilePreviewRoute outside={false} />
    </CabinetShell>
  ) : (
    <main>
      <CornerLanguagePicker />
      <ProfilePreviewRoute outside />
    </main>
  );
}

/* ============================================= the pages in their own frame, in the path list */

/**
 * The two pages mounted below `CABINET_ROUTE_PATHS`' cited lines, as that list names them: "Preview
 * your public page" (design 24, T28) and the welcome (design 25). A function declaration, hoisted,
 * so the list reads them without a line of it moving.
 */
function ownFramePaths(): readonly string[] {
  return [PROFILE_PREVIEW_ROUTE.path, WELCOME_PATH];
}
